import {
  get,
  getNum,
  getStr,
  getTable,
  isEmail,
  isPhone,
  isWill,
  isYes,
  lt,
  toDate,
  toPct,
} from '@shared';

// R1: Beneficiary percentages must sum to 100% within each designation type (Primary & Contingent).
const ruleBeneficiaryPercentSums: Rule = {
  id: 'beneficiary.percent.sum',
  check: (form): RuleResult => {
    const rows = getTable(form, 'beneficiaryDesignations');
    const sumByType: Record<string, number> = {};
    let badCell = false;
    for (const r of rows) {
      const pctRaw = String(r['designation-percent'] ?? '');
      const pct = toPct(pctRaw);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        badCell = true;
        break;
      }
      const type = String(r['designation-type'] ?? '').toLowerCase(); // 'primary' | 'contingent'
      sumByType[type] = (sumByType[type] ?? 0) + pct;
    }
    if (badCell) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_PERCENT_INVALID',
        message: 'Each beneficiary % must be a number between 0 and 100.',
        refs: ['beneficiaryDesignations'],
        category: 'formatting',
      };
    }
    const errors: string[] = [];
    (['primary', 'contingent'] as const).forEach(k => {
      if ((sumByType[k] ?? 0) > 0 && Math.round(sumByType[k] ?? 0) !== 100) {
        errors.push(`${k} = ${sumByType[k] ?? 0}%`);
      }
    });
    if (errors.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_SUM_100',
        message: `Beneficiary percentages must total 100% separately for Primary and Contingent (${errors.join(
          '; ',
        )}).`,
        refs: ['beneficiaryDesignations'],
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R2: If any insurance “yes” flags are set, coverage table rows must be coherent when present.
//     (UI already handles visibility/required; we just validate filled rows internally.)
const yesFlagsPrimary = [
  'primaryInsuredOtherInsurance',
  'primaryInsuredSoldPolicy',
  'primaryInsuredRecentApplications',
  'primaryInsuredReplacement',
  'primaryInsuredPastInsurance',
  'primaryInsuredOtherLtc',
  'primaryInsuredTerminatedLtc',
  'primaryInsuredIntendToStop',
  'primaryInsuredReplaceMedical',
];
const yesFlagsAdditional = [
  'additionalInsuredOtherInsurance',
  'additionalInsuredSoldPolicy',
  'additionalInsuredRecentApplications',
  'additionalInsuredReplacement',
  'additionalInsuredPastInsurance',
  'additionalInsuredOtherLtc',
  'additionalInsuredTerminatedLtc',
  'additionalInsuredIntendToStop',
  'additionalInsuredReplaceMedical',
];

const ruleInsuranceCoverageTableCoherence: Rule = {
  id: 'insurance.coverage.table.coherence',
  when: form => [...yesFlagsPrimary, ...yesFlagsAdditional].some(f => isYes(get(form, f))),
  check: (form): RuleResult => {
    const rows = getTable(form, 'insuranceCoverageDetails');
    if (!rows.length) return { ok: true }; // section visibility/required handled by UI
    const errs: string[] = [];
    rows.forEach((r, i) => {
      const idx = i + 1;
      // If “to be replaced” = Yes, Company & Policy Number should be present (content sanity)
      if (isYes(r['to-be-replaced'])) {
        if (!String(r['company'] ?? '').trim())
          errs.push(`Row ${idx}: Company required when "To Be Replaced" is Yes.`);
        if (!String(r['policy-number'] ?? '').trim())
          errs.push(`Row ${idx}: Policy Number required when "To Be Replaced" is Yes.`);
      }
      // Year Issued sanity
      const y = String(r['year-issued'] ?? '').trim();
      if (y && !/^\d{4}$/.test(y)) errs.push(`Row ${idx}: Year Issued must be YYYY.`);
      // Amount numeric sanity
      const amt = getNum({ amt: r['amount'] }, 'amt');
      if (String(r['amount'] ?? '').trim() && !Number.isFinite(amt))
        errs.push(`Row ${idx}: Amount must be a valid number.`);
    });
    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'INSURANCE_TABLE_SANITY',
        message: errs.map(e => `• ${e}`).join('\n'),
        refs: ['insuranceCoverageDetails'],
        category: 'formatting',
      };
    }
    return { ok: true };
  },
};

// R3: Citizenship documents sanity (issue < expiration). Applies to insured/owner/joint owner where present.
const docPairs: { issue: string; exp: string; holder: string }[] = [
  {
    issue: 'proposedInsuredGreenCardIssueDate',
    exp: 'proposedInsuredGreenCardExpirationDate',
    holder: 'Proposed Insured Green Card',
  },
  {
    issue: 'proposedInsuredVisaIssueDate',
    exp: 'proposedInsuredVisaExpirationDate',
    holder: 'Proposed Insured Visa',
  },
  {
    issue: 'policyOwnerGreenCardIssueDate',
    exp: 'policyOwnerGreenCardExpirationDate',
    holder: 'Policy Owner Green Card',
  },
  {
    issue: 'policyOwnerVisaIssueDate',
    exp: 'policyOwnerVisaExpirationDate',
    holder: 'Policy Owner Visa',
  },
  {
    issue: 'jointPolicyOwnerGreenCardIssueDate',
    exp: 'jointPolicyOwnerGreenCardExpirationDate',
    holder: 'Joint Owner Green Card',
  },
  {
    issue: 'jointPolicyOwnerVisaIssueDate',
    exp: 'jointPolicyOwnerVisaExpirationDate',
    holder: 'Joint Owner Visa',
  },
  {
    issue: 'additionalInsuredGreenCardIssueDate',
    exp: 'additionalInsuredGreenCardExpirationDate',
    holder: 'Additional Insured Green Card',
  },
  {
    issue: 'additionalInsuredVisaIssueDate',
    exp: 'additionalInsuredVisaExpirationDate',
    holder: 'Additional Insured Visa',
  },
];

const ruleDocDateOrder: Rule = {
  id: 'citizenship.docs.date.order',
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];
    for (const p of docPairs) {
      const i = toDate(get(form, p.issue));
      const e = toDate(get(form, p.exp));
      if (i && e) {
        if (!lt(i, e)) {
          errs.push(`${p.holder}: Issue date must be before expiration date.`);
          refs.push(p.issue, p.exp);
        }
      }
    }
    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'DOC_DATE_ORDER',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R4: US residence years/months sanity (primary & additional when present).
const ruleUSResidenceSanity: Rule = {
  id: 'us.residence.sanity',
  check: (form): RuleResult => {
    const pairs = [
      ['proposedInsuredUsResidenceYears', 'proposedInsuredUsResidenceMonths'],
      ['additionalInsuredUsResidenceYears', 'additionalInsuredUsResidenceMonths'],
    ] as const;
    const errs: string[] = [];
    const refs: string[] = [];
    for (const [yrsRef, mosRef] of pairs) {
      // Only validate if the fields exist in the form
      const hasYrsField = yrsRef in form;
      const hasMosField = mosRef in form;

      if (!hasYrsField && !hasMosField) {
        continue; // Skip this pair if neither field exists in the form
      }

      const yrs = getNum(form, yrsRef);
      const mos = getNum(form, mosRef);
      // Only validate if either is present and has a value
      const yrsSet = !Number.isNaN(yrs);
      const mosSet = !Number.isNaN(mos);
      if (yrsSet || mosSet) {
        if (yrsSet && yrs < 0) {
          errs.push(`Years in U.S. cannot be negative.`);
          refs.push(yrsRef);
        }
        if (mosSet && (mos < 0 || mos > 11)) {
          errs.push(`Months in U.S. must be between 0 and 11.`);
          refs.push(mosRef);
        }
        if ((yrs || 0) + (mos || 0) === 0) {
          errs.push(`Total time in U.S. must be greater than zero.`);
          refs.push(yrsRef, mosRef);
        }
      }
    }
    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'US_RESIDENCE_RANGE',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R5: Premium option math sanity (+ temp insurance limits)
const rulePremiumSanity: Rule = {
  id: 'premium.sanity',
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const premiumOption = String(get(form, 'premiumOption') ?? '');
    if (premiumOption === 'option1') {
      const single = getNum(form, 'singlePremiumAmount');
      if (!Number.isNaN(single) && single <= 0) {
        errs.push('Single Premium Amount must be greater than 0.');
        refs.push('singlePremiumAmount');
      }
    }
    if (premiumOption === 'option2') {
      const lvl = getNum(form, 'levelPremiumAmount');
      if (!Number.isNaN(lvl) && lvl <= 0) {
        errs.push('Level Premium Amount must be greater than 0.');
        refs.push('levelPremiumAmount');
      }
    }
    if (premiumOption === 'option3') {
      const ls = getNum(form, 'lumpSumAmount');
      const combo = getNum(form, 'combinedLevelPremiumAmount');
      if (!Number.isNaN(ls) && ls <= 0) {
        errs.push('Lump Sum Amount must be greater than 0.');
        refs.push('lumpSumAmount');
      }
      if (!Number.isNaN(combo) && combo <= 0) {
        errs.push('Combined Level Premium Amount must be greater than 0.');
        refs.push('combinedLevelPremiumAmount');
      }
      // Start month <= 6 for monthly; Start year <= 2 for annual (when present)
      const freq = String(get(form, 'op3PaymentFrequency') ?? '');
      if (freq === 'monthly') {
        const m = getNum(form, 'levelPremiumStartMonth');
        if (!Number.isNaN(m) && (m < 1 || m > 6)) {
          errs.push('For monthly frequency, start month must be 1–6.');
          refs.push('levelPremiumStartMonth');
        }
      }
      if (freq === 'annually') {
        const y = getNum(form, 'levelPremiumStartYear');
        if (!Number.isNaN(y) && (y < 1 || y > 2)) {
          errs.push('For annual frequency, start year must be 1–2.');
          refs.push('levelPremiumStartYear');
        }
      }
    }

    // Future billing amounts (positive when selected)
    if (isYes(get(form, 'directBill'))) {
      const n = getNum(form, 'directBillAmount');
      if (!Number.isNaN(n) && n <= 0) {
        errs.push('Direct Bill Amount must be greater than 0.');
        refs.push('directBillAmount');
      }
    }
    if (isYes(get(form, 'listBill'))) {
      const n = getNum(form, 'listBillAmount');
      if (!Number.isNaN(n) && n <= 0) {
        errs.push('List Bill Amount must be greater than 0.');
        refs.push('listBillAmount');
      }
    }
    if (isYes(get(form, 'fundedByAnnuity'))) {
      const n = getNum(form, 'fundedByAnnuityAmount');
      if (!Number.isNaN(n) && n <= 0) {
        errs.push('Annuity Funding Amount must be greater than 0.');
        refs.push('fundedByAnnuityAmount');
      }
    }

    // Advance Electronic Debit amount sanity
    if (String(get(form, 'advancePaymentMethod') ?? '') === 'electronic-debit') {
      const n = getNum(form, 'electronicDebitAmount');
      if (!Number.isNaN(n) && n <= 0) {
        errs.push('Electronic Debit Amount must be greater than 0.');
        refs.push('electronicDebitAmount');
      }
    }

    // Temporary Insurance Agreement limits
    const tiaPay = getNum(form, 'tempInsurancePayment');
    const tiaBase = getNum(form, 'tempInsuranceBaseAmount');
    if (!Number.isNaN(tiaPay) && !Number.isNaN(tiaBase)) {
      if (tiaPay > tiaBase) {
        errs.push(
          'Advance payment cannot exceed the base specified amount for Temporary Insurance.',
        );
        refs.push('tempInsurancePayment', 'tempInsuranceBaseAmount');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PREMIUM_SANITY',
        message: errs.map(e => `• ${e}`).join('\n'),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R6: Cross-consistency: If the Financial Professional/Agent certifies “will replace” or “does/does-not”,
//     it must not contradict applicant answers.
const ruleReplacementConsistency: Rule = {
  id: 'replacement.consistency',
  when: form =>
    // apply if any of the certification radios exist on the form
    get(form, 'insuranceWillReplace') != null || get(form, 'existingInsurance') != null,
  check: (form): RuleResult => {
    const applicantAnyYes = [...yesFlagsPrimary, ...yesFlagsAdditional].some(f =>
      isYes(get(form, f)),
    );
    const willReplace = isWill(get(form, 'insuranceWillReplace'));
    const conflicts: string[] = [];

    if (willReplace && !applicantAnyYes) {
      conflicts.push(
        'Agent/FP marked “Will replace” but applicant replacement/coverage answers are all No.',
      );
    }
    if (!willReplace && applicantAnyYes) {
      conflicts.push(
        'Agent/FP marked “Will not replace” but applicant indicated existing/replacement activity.',
      );
    }

    if (conflicts.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPLACEMENT_CONFLICT',
        message: conflicts.join(' '),
        refs: [
          'insuranceWillReplace',
          'existingInsurance',
          'insuranceCoverageDetails',
          ...yesFlagsPrimary,
          ...yesFlagsAdditional,
        ],
        category: 'general',
      };
    }
    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC ADD-ONS -------------------------------------------

// CA: Tobacco & Health condition gating (advisory per guide). Also allow "tobaccoAdditional" only when Together.
const ruleCA_TobaccoAndHealthAdvisory: Rule = {
  id: 'ca.tobacco.health.advisory',
  states: ['CA'],
  check: (form): RuleResult => {
    const advisories: string[] = [];
    const refs: string[] = [];

    // Tobacco fields exist in CA variant
    const tp = get(form, 'tobaccoPrimary');
    if (tp != null && isYes(tp)) {
      advisories.push(
        'Primary Insured reported tobacco/nicotine use in past 12 months (tobacco rates likely).',
      );
      refs.push('tobaccoPrimary');
    }
    const ta = get(form, 'tobaccoAdditional');
    if (ta != null && isYes(ta)) {
      advisories.push(
        'Additional Insured reported tobacco/nicotine use in past 12 months (tobacco rates likely).',
      );
      refs.push('tobaccoAdditional');
    }

    // Health Conditions (Primary/Additional) – if any “Yes”, raise carrier_eligibility_risk (per Nationwide prequal guide)
    const primaryHealthRefs = [
      'primaryDiabetes',
      'primaryStroke',
      'primaryCancer',
      'primaryHeartDisease',
      'primaryAlzheimers',
      'primaryDementia',
      'primaryAlcoholism',
      'primaryDrugAbuse',
      'primaryKidneyDisease',
      'primaryAIDS',
    ];
    const additionalHealthRefs = [
      'additionalDiabetes',
      'additionalStroke',
      'additionalCancer',
      'additionalHeartDisease',
      'additionalAlzheimers',
      'additionalDementia',
      'additionalAlcoholism',
      'additionalDrugAbuse',
      'additionalKidneyDisease',
      'additionalAIDS',
    ];

    if (primaryHealthRefs.some(r => isYes(get(form, r)))) {
      advisories.push(
        'Primary Insured answered “Yes” to at least one serious health condition; application may be declined per prequalification guidance.',
      );
      refs.push(...primaryHealthRefs);
    }
    if (additionalHealthRefs.some(r => isYes(get(form, r)))) {
      advisories.push(
        'Additional Insured answered “Yes” to at least one serious health condition; application may be declined per prequalification guidance.',
      );
      refs.push(...additionalHealthRefs);
    }

    if (advisories.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'CA_TOB_HEALTH_RISK',
        message: advisories.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'carrier-requirements',
      };
    }
    return { ok: true };
  },
};

// AZ: Financial Professional narrative present & consistent with applicant answers (fields exist only in AZ/IN).
const ruleAZ_FP_NarrativeConsistency: Rule = {
  id: 'az.fp.narrative.consistency',
  states: ['AZ', 'IN'],
  check: (form): RuleResult => {
    const msgs: string[] = [];
    const refs: string[] = [];
    const applicantAnyYes = [...yesFlagsPrimary, ...yesFlagsAdditional].some(f =>
      isYes(get(form, f)),
    );

    const existsRadio = get(form, 'existingInsurance'); // Does / Does Not
    if (!existsRadio && applicantAnyYes) {
      msgs.push(
        'Applicant indicated existing/replacement activity; ensure FP section reflects that.',
      );
      refs.push(
        'financialProfessionalHealthPolicies',
        'financialProfessionalActivePolicies',
        'financialProfessionalExpiredPolicies',
        'existingInsurance',
      );
    }

    if (msgs.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'AZ_FP_CONSISTENCY',
        message: msgs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'general',
      };
    }
    return { ok: true };
  },
};

// FL: Agent credentials/contacts sanity (license pattern, email, phone). Fields exist only in FL.
const ruleFL_AgentCredentials: Rule = {
  id: 'fl.agent.credentials',
  states: ['FL'],
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const email = getStr(form, 'agentEmailCert');
    if (email && !isEmail(email)) {
      errs.push('Agent email address format looks invalid.');
      refs.push('agentEmailCert');
    }

    const phone = getStr(form, 'agentPhone');
    if (phone && !isPhone(phone)) {
      errs.push('Agent phone number format looks invalid.');
      refs.push('agentPhone');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'FL_AGENT_CONTACTS',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }
    return { ok: true };
  },
};

// ---------- EXPORT ------------------------------------------------------------

export const nationwideAllStatesRules: Rule[] = [
  // General rules
  ruleBeneficiaryPercentSums,
  ruleInsuranceCoverageTableCoherence,
  ruleDocDateOrder,
  ruleUSResidenceSanity,
  rulePremiumSanity,
  ruleReplacementConsistency,

  // State add-ons (apply only if ctx.state matches)
  ruleCA_TobaccoAndHealthAdvisory,
  ruleAZ_FP_NarrativeConsistency,
  ruleFL_AgentCredentials,
];

// Example evaluator (use your own pipeline if you already have one)
export function evaluateNWNigo(form: PolicyFormSubmissionData, state?: string): RuleResult[] {
  return nationwideAllStatesRules
    .filter(r => !r.states || !state || r.states.includes(state))
    .filter(r => (r.when ? r.when(form) : true))
    .map(r => {
      try {
        return r.check(form);
      } catch (e) {
        return {
          ok: false,
          severity: 'high-confidence' as const,
          code: 'RULE_RUNTIME',
          message: `Rule ${r.id} failed: ${String(e)}`,
          category: 'general' as const,
        };
      }
    })
    .filter(res => !res.ok);
}
